import { Card, IconButton, type IconComponent, LuIcon, Separator, Spinner, Text } from "@overmap-ai/blocks"
import { pluralize } from "@overmap-ai/common"
import {
	type FullOfflineRequestDetails,
	getOutboxCoordinator,
	HttpMethod,
	markForDeletion,
	OUTBOX_RETRY_DELAY,
	selectDeletedRequests,
	selectLatestRetryTime,
	selectOutboxActive,
	selectOutboxBusy,
	selectOutboxRequests,
	selectOutboxRetryActive,
} from "@overmap-ai/core"
import { amber, green, jade, red } from "@radix-ui/colors"
import { Circle } from "lucide-react"
import React, { memo, useCallback, useEffect, useMemo, useState } from "react"

import { useConfirmDeleteDialog } from "../../hooks/utils"
import { modelIconMapping } from "../../mappings/icons"
import { useAppDispatch, useAppSelector } from "../../store"

const methodToIconMapping: Record<HttpMethod, { color: string; icon: IconComponent }> = {
	[HttpMethod.GET]: { color: jade.jade9, icon: modelIconMapping.download },
	[HttpMethod.POST]: { color: green.green9, icon: modelIconMapping.add },
	[HttpMethod.PATCH]: { color: amber.amber9, icon: modelIconMapping.edit },
	[HttpMethod.DELETE]: { color: red.red9, icon: modelIconMapping.delete },
	[HttpMethod.PUT]: { color: amber.amber9, icon: modelIconMapping.edit },
}

interface OnlineIndicatorProps {
	isOnline: boolean
}

const OnlineIndicator = memo((props: OnlineIndicatorProps) => {
	const { isOnline } = props
	return (
		<div className="flex items-center gap-2">
			<Text size="sm" accentColor="base">
				{isOnline ? "Online" : "Offline"}
			</Text>
			<LuIcon
				icon={Circle}
				size="xs"
				color={isOnline ? jade.jade9 : red.red9}
				fill={isOnline ? jade.jade9 : red.red9}
			/>
		</div>
	)
})
OnlineIndicator.displayName = "OnlineIndicator"

interface OutboxItemProps {
	action: FullOfflineRequestDetails
	onDelete: (uuid: string) => void
}

const OutboxItem = memo((props: OutboxItemProps) => {
	const { onDelete, action } = props
	const request = action.request
	const { icon, color } = methodToIconMapping[request.method]
	const timestamp = new Date(action.timestamp)

	const handleDelete = useCallback(() => {
		onDelete(request.uuid)
	}, [request.uuid, onDelete])

	return (
		<Card accentColor="base" size="sm" className="w-xs">
			<div className="grid grid-rows-2 gap-2">
				<div className="flex items-center justify-between gap-2">
					<div className="flex items-center gap-2">
						<LuIcon icon={icon} color={color} size="sm" />
						<Text size="sm" accentColor="base">
							{timestamp.toLocaleString(undefined, {
								month: "long",
								day: "numeric",
								year: "numeric",
								hour: "numeric",
								minute: "numeric",
								second: "numeric",
							})}
						</Text>
					</div>

					<IconButton size="sm" aria-label="delete" type="button" onClick={handleDelete}>
						<LuIcon icon={modelIconMapping.delete} />
					</IconButton>
				</div>
				<Text>{request.description}</Text>
			</div>
		</Card>
	)
})
OutboxItem.displayName = "OutboxItem"

interface OutboxLaneProps {
	requests: FullOfflineRequestDetails[]
	onDelete: OutboxItemProps["onDelete"]
}

const OutboxLane = memo((props: OutboxLaneProps) => {
	const { requests, onDelete } = props

	const nbItems = requests.length
	const nbUniqueUuids = new Set(requests.map((item) => item.request.uuid)).size

	if (nbItems !== nbUniqueUuids) {
		throw new Error("Non-unique UUIDs detected")
	}

	return (
		<div className="relative flex flex-col">
			{requests.map((item, index) => {
				return (
					<React.Fragment key={item.request.uuid}>
						{index !== 0 && (
							<Separator className="self-center" orientation="vertical" size="sm" decorative />
						)}
						<OutboxItem onDelete={onDelete} action={item} />
					</React.Fragment>
				)
			})}
		</div>
	)
})
OutboxLane.displayName = "OutboxLane"

const OutboxViewer = memo(() => {
	const dispatch = useAppDispatch()
	const deletedRequests = useAppSelector(selectDeletedRequests)
	const items = useAppSelector(selectOutboxRequests)
	const isBusy = useAppSelector(selectOutboxBusy)
	const retryScheduled = useAppSelector(selectOutboxRetryActive)
	const lastRetryTimestamp: number = useAppSelector(selectLatestRetryTime)
	const online = useAppSelector(selectOutboxActive)
	const coordinator = getOutboxCoordinator()
	const openConfirmDeleteDialog = useConfirmDeleteDialog()

	const lanes = useMemo(() => {
		if (!coordinator) {
			return null
		}
		/*
        1. Get a list of all outbox items
        2. Get a list of ready outbox items
        3. For each ready outbox item, get a list of outbox items that depend on it
        4. We now have multiple lanes
        5. Find any outbox item not in a lane
        6. Render those outbox items in a "waiting area" or just throw an error
         */
		const ready = coordinator.getReady()
		const ret: FullOfflineRequestDetails[][] = []

		// Items can have blockers in multiple lanes. We just pick the first lane that has a blocker and add it to that,
		// which avoids the situation in which a request is duplicated in two lanes (because two leaf nodes have it as a
		// dependency, and each leaf node gets its own lane). This is not ideal, but 90% of the time, there will be
		// fewer than 3 lanes, and most requests will only have blockers in one lane.
		const includedItems = new Set<string>(deletedRequests)

		for (const item of ready) {
			if (deletedRequests.includes(item.request.uuid)) {
				// Don't render deleted requests
				continue
			}
			const dependents = coordinator.graph.dependentsOf(item.request.uuid)

			const dependentItems = dependents
				.filter((uuid) => !includedItems.has(uuid))
				.map((uuid) => coordinator.graph.getNodeData(uuid))
				// Sort by timestamp, or we might see deletions appear before edits of the same item, even if the
				// deletion depends on the edit. We could avoid this by recursively getting dependents of dependents,
				// but that would be a lot of work for a rare edge case. The important thing is that the items are
				// processed in the correct order.
				.sort((a, b) => {
					return a.timestamp.localeCompare(b.timestamp)
				})

			includedItems.add(item.request.uuid)
			for (const dependentItem of dependentItems) {
				includedItems.add(dependentItem.request.uuid)
			}
			const lane = [item, ...dependentItems]
			ret.push(lane)
		}
		// Sort lanes by the timestamp of the first element
		return ret.sort((a, b) => {
			const aTimestamp: string = a[0]!.timestamp
			const bTimestamp: string = b[0]!.timestamp
			return aTimestamp.localeCompare(bTimestamp)
		})
	}, [coordinator, deletedRequests])

	const handleDeleteConfirmed = useCallback(
		(uuid: string, dependents: string[]) => {
			dispatch(markForDeletion(uuid))
			for (const dependent of dependents) {
				dispatch(markForDeletion(dependent))
			}
		},
		[dispatch],
	)

	const handleDeleteRequest = useCallback(
		(uuid: string) => {
			if (!coordinator) {
				throw new Error("Coordinator is not initialized")
			}
			const dependents = coordinator.graph.dependentsOf(uuid).filter((uuid) => !deletedRequests.includes(uuid))
			const dependentsNote = dependents.length
				? ` and ${dependents.length} dependent ${pluralize("request", dependents.length)}`
				: ""

			openConfirmDeleteDialog({
				title: "Cancel request",
				description: `Are you sure you want to cancel this request${dependentsNote}? This cannot be undone. 
				Your local state may become inconsistent with the server.`,
				onAction: () => handleDeleteConfirmed(uuid, dependents),
			})
		},
		[openConfirmDeleteDialog, coordinator, deletedRequests, handleDeleteConfirmed],
	)

	// Unix timestamp (milliseconds) of the last retry attempt.
	// We will use this to show a countdown to the next retry.
	const retryDelay: number = OUTBOX_RETRY_DELAY // milliseconds
	const millisecondsSinceLastRetry: number = Date.now() - lastRetryTimestamp
	// If retryDelay = 5, then count down: 5, 4, 3, 2, 1, then a spinner, then 5, 4, 3, 2, 1, etc.
	const remainingSeconds: number = Math.min(
		Math.round(retryDelay / 1000),
		Math.max(1, Math.ceil((retryDelay - millisecondsSinceLastRetry) / 1000)),
	)

	// Incremented by a timer to rerender every second
	const [timerKey, setTimerKey] = useState(0)

	useEffect(() => {
		if (retryScheduled || isBusy) {
			const interval = setInterval(() => {
				setTimerKey((prev) => prev + 1)
			}, 1000)
			return () => clearInterval(interval)
		}
	}, [retryScheduled, isBusy, timerKey])

	return (
		<div className="flex w-full flex-col gap-2 overflow-hidden">
			<div className="flex items-center justify-between gap-2">
				{isBusy ? (
					<div className="flex items-center gap-2">
						<Spinner size="sm" />
						<Text size="sm">Working…</Text>
					</div>
				) : retryScheduled ? (
					<Text size="sm">
						Retrying in <Text accentColor="yellow">{remainingSeconds}</Text>
					</Text>
				) : (
					<Text size="sm">Idle</Text>
				)}

				<OnlineIndicator isOnline={online} />
			</div>
			<div className="flex w-full grow gap-4 overflow-x-auto overflow-y-auto">
				{lanes?.map((lane, index) => (
					<OutboxLane key={index} onDelete={handleDeleteRequest} requests={lane} />
				))}
			</div>
			{!items.length && <p className="mt-0">The outbox is empty</p>}
		</div>
	)
})

OutboxViewer.displayName = "OutboxViewer"

export default OutboxViewer
